import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, User, Search, X, ToggleRight, ToggleLeft, Menu } from 'lucide-react';
import UniversalSearchBar from '../UniversalSearchBar';
import LoadChecker from '../LoadChecker';

const Navbar2 = () => {
    const [controller, setController] = useState(false);
    const [profile, setProfile] = useState(false);
    const [notification, setNotification] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const menuRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const path = location.pathname.replace(/\//g, '');
        setController(path === 'controller');
        setProfile(path === 'profile');
        setNotification(path === 'notification');
    }, [location.pathname]);

    const handleNavigation = useCallback((path) => {
        navigate(path);
        setMenuOpen(false);
        setSearchOpen(false);
    }, [navigate]);

    return (
        <header className="fixed top-0 left-0 right-0 z-40">
            {/* Main Navbar */}
            <div className="bg-white/85 backdrop-blur-xl border-b border-main-border/70 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.18)]">
                {/* Add left padding to account for sidebar (70px collapsed) */}
                <div className={`flex items-center justify-between h-[72px] px-4 md:px-6 ${!isMobile ? 'ml-[70px]' : ''} gap-4`}>

                    {/* Center Section - Controller, Search, LoadChecker (Desktop) */}
                    <div className="hidden md:flex items-center gap-4 flex-1 w-full">
                        {/* Controller Toggle */}
                        <ControllerButton
                            isActive={controller}
                            onClick={() => handleNavigation('/controller')}
                        />

                        {/* Search Bar */}
                        <div className="flex-1 max-w-md">
                            <UniversalSearchBar />
                        </div>

                        {/* Server Status - LoadChecker */}
                        <div className="hidden lg:block ml-auto">
                            <LoadChecker />
                        </div>
                    </div>

                    {/* Right Section - Actions (Desktop) */}
                    <div className="hidden md:flex items-center gap-1 ml-auto">
                        <NavIconButton
                            icon={<Bell size={20} fill={notification ? 'currentColor' : 'none'} />}
                            isActive={notification}
                            onClick={() => handleNavigation('/notification')}
                            tooltip="Notifications"
                        />
                        <NavIconButton
                            icon={<User size={20} />}
                            isActive={profile}
                            onClick={() => handleNavigation('/profile')}
                            tooltip="Profile"
                        />
                        <div className="w-px h-6 mx-2 bg-main-border" />
                        <NavIconButton
                            icon={<LogOut size={20} />}
                            onClick={() => handleNavigation('/Logout')}
                            variant="danger"
                            tooltip="Logout"
                        />
                    </div>

                    {/* Mobile Actions */}
                    {isMobile && (
                        <div className="flex items-center gap-2 ml-auto">
                            {/* Mobile Search Toggle */}
                            <button
                                onClick={() => setSearchOpen(!searchOpen)}
                                style={{
                                    backgroundColor: searchOpen ? '#34C759' : '#F9FAFE',
                                    color: searchOpen ? '#FFFFFF' : '#888B91'
                                }}
                                className="p-2.5 rounded-xl transition-all duration-200 flex-shrink-0 "
                            >
                                {searchOpen ? <X size={20} /> : <Search size={20} />}
                            </button>

                            {/* Mobile Menu Toggle */}
                            <div className="relative flex-shrink-0" ref={menuRef}>
                                <button
                                    onClick={() => setMenuOpen(!menuOpen)}
                                    style={{
                                        backgroundColor: menuOpen ? '#34C759' : '#F9FAFE',
                                        color: menuOpen ? '#FFFFFF' : '#888B91'
                                    }}
                                    className="p-2.5 rounded-xl transition-all duration-200"
                                >
                                    <Menu size={22} className={`transition-transform duration-300 ${menuOpen ? 'rotate-90' : ''}`} />
                                </button>

                                {/* Mobile Dropdown */}
                                <div
                                    className={`
                                        absolute right-0 top-full mt-3 w-72
                                        bg-white/95 backdrop-blur-xl border border-main-border/70
                                        rounded-2xl shadow-xl overflow-hidden
                                        transition-all duration-300 origin-top-right
                                        ${menuOpen
                                            ? 'opacity-100 scale-100 translate-y-0'
                                            : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                                        }
                                    `}
                                >
                                    {/* User Profile Header */}
                                    <div className="relative p-5 text-white overflow-hidden bg-gradient-to-br from-main-blue to-emerald-500">
                                        <div className="relative flex items-center gap-4">
                                            <div
                                                style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
                                                className="w-12 h-12 rounded-xl flex items-center justify-center ring-2 ring-white/30"
                                            >
                                                <User size={24} />
                                            </div>
                                            <div>
                                                <p className="font-semibold">Admin User</p>
                                                <p className="text-sm text-white/70">Administrator</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Menu Items */}
                                    <div className="p-3">
                                        <MobileMenuItem
                                            icon={controller
                                                ? <ToggleRight size={22} style={{ color: '#34C759' }} />
                                                : <ToggleLeft size={22} />
                                            }
                                            label="Controller"
                                            sublabel={controller ? 'Active' : 'Inactive'}
                                            isActive={controller}
                                            onClick={() => handleNavigation('/controller')}
                                        />
                                        <MobileMenuItem
                                            icon={<User size={20} />}
                                            label="Profile"
                                            sublabel="Manage account"
                                            isActive={profile}
                                            onClick={() => handleNavigation('/profile')}
                                        />
                                        <MobileMenuItem
                                            icon={<Bell size={20} />}
                                            label="Notifications"
                                            sublabel="3 new alerts"
                                            isActive={notification}
                                            onClick={() => handleNavigation('/notification')}
                                        />

                                        {/* Divider */}
                                        <div style={{ borderColor: '#CFD1D3' }} className="my-3 border-t" />

                                        {/* Server Status */}
                                        <div className="px-3 py-2 mb-2">
                                            <p style={{ color: '#888B91' }} className="text-xs font-semibold uppercase tracking-wider mb-3">
                                                System Status
                                            </p>
                                            <LoadChecker isMobile={true} />
                                        </div>

                                        {/* Divider */}
                                        <div style={{ borderColor: '#CFD1D3' }} className="my-3 border-t" />

                                        <MobileMenuItem
                                            icon={<LogOut size={20} />}
                                            label="Sign Out"
                                            sublabel="End session"
                                            onClick={() => handleNavigation('/Logout')}
                                            variant="danger"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Search Expanded */}
                {isMobile && searchOpen && (
                    <div className="px-4 pb-4 pt-2">
                        <UniversalSearchBar />
                    </div>
                )}
            </div>
        </header>
    );
};

// Desktop Nav Icon Button
const NavIconButton = ({ icon, isActive, onClick, variant = 'default', tooltip }) => {
    const getStyles = () => {
        if (variant === 'danger') {
            return {
                backgroundColor: 'transparent',
                color: '#888B91'
            };
        }
        if (isActive) {
            return {
                backgroundColor: '#34C759',
                color: '#FFFFFF'
            };
        }
        return {
            backgroundColor: 'transparent',
            color: '#888B91'
        };
    };

    const styles = getStyles();

    return (
        <button
            onClick={onClick}
            style={styles}
            className={`
                relative p-2.5 rounded-xl transition-all duration-200 group
                ${isActive && variant !== 'danger' ? 'shadow-lg shadow-main-blue/30' : ''}
                ${variant === 'danger' ? 'hover:bg-red-50 hover:text-red-500' : !isActive ? 'hover:bg-main-th hover:text-main-blue' : ''}
            `}
        >
            {icon}

            {/* Tooltip */}
            {tooltip && (
                <span
                    style={{ backgroundColor: '#111723' }}
                    className="absolute -bottom-9 left-1/2 -translate-x-1/2 px-2 py-1
                               text-white text-xs font-medium rounded-lg
                               opacity-0 group-hover:opacity-100 transition-opacity duration-200
                               whitespace-nowrap pointer-events-none z-50"
                >
                    {tooltip}
                    <span style={{ backgroundColor: '#111723' }} className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45" />
                </span>
            )}
        </button>
    );
};

// Controller Toggle Button
const ControllerButton = ({ isActive, onClick }) => {
    return (
        <button
            onClick={onClick}
            style={{
                backgroundColor: isActive ? '#34C759' : '#F9FAFE',
                color: isActive ? '#FFFFFF' : '#888B91',
                borderColor: isActive ? '#34C759' : '#CFD1D3'
            }}
            className={`relative flex items-center gap-2.5 px-4 py-2 rounded-xl
                       transition-all duration-300 font-medium text-sm overflow-hidden group flex-shrink-0 border
                       ${isActive ? 'shadow-lg shadow-main-blue/30' : 'hover:border-main-blue/40'}`}
        >
            <span className={`relative transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}>
                {isActive
                    ? <ToggleRight size={24} />
                    : <ToggleLeft size={24} style={{ color: '#34C759' }} />
                }
            </span>
            {/* <span className="relative">Controller</span> */}
        </button>
    );
};

// Mobile Menu Item
const MobileMenuItem = ({ icon, label, sublabel, isActive, onClick, variant = 'default' }) => {
    const getStyles = () => {
        if (variant === 'danger') {
            return {
                color: '#F34235'
            };
        }
        if (isActive) {
            return {
                backgroundColor: '#f2fcf5',
                color: '#34C759'
            };
        }
        return {
            color: '#111723'
        };
    };

    const getIconBgStyles = () => {
        if (variant === 'danger') {
            return {
                backgroundColor: '#FEE2E2',
                color: '#F34235'
            };
        }
        if (isActive) {
            return {
                backgroundColor: '#d2fcde',
                color: '#34C759'
            };
        }
        return {
            backgroundColor: '#F9FAFE',
            color: '#888B91'
        };
    };

    return (
        <button
            onClick={onClick}
            style={getStyles()}
            className={`
                w-full flex items-center gap-3 px-3 py-3 rounded-xl
                transition-all duration-200 text-left group
                ${variant === 'danger' ? 'hover:bg-red-50' : !isActive ? 'hover:bg-gray-50' : ''}
            `}
        >
            <div
                style={getIconBgStyles()}
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200"
            >
                {icon}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{label}</p>
                {sublabel && (
                    <p style={{ color: variant === 'danger' ? '#F34235' : '#888B91' }} className="text-xs mt-0.5">
                        {sublabel}
                    </p>
                )}
            </div>

            {/* Active Indicator */}
            {isActive && variant !== 'danger' && (
                <span style={{ backgroundColor: '#34C759' }} className="w-2 h-2 rounded-full flex-shrink-0" />
            )}
        </button>
    );
};

export default Navbar2;
